import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrongRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import { Box, Dialog, IconButton, Tooltip } from "@mui/material";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

interface FullscreenDiagramProps {
  open: boolean;
  onClose: () => void;
  /**
   * The already-sanitised SVG markup captured from MermaidDiagram's
   * inline render. Injected via dangerouslySetInnerHTML — DOMPurify ran
   * on the upstream side so this is safe.
   */
  svgMarkup: string;
}

const _MIN_SCALE = 0.1;
const _MAX_SCALE = 30;
const _ZOOM_SENSITIVITY = 0.0015; // wheel-delta multiplier; tuned for trackpads
// Default zoom multiplier on top of the fit-to-screen scale. Mermaid
// SVGs intrinsically render small (legends, labels at 12-14px in viewBox
// units), so the strict fit-to-view of 0.9× felt zoomed-out. 2.4× lands
// the diagram at "readable text" by default; the user can scroll-zoom
// out or hit reset (which now also uses this multiplier) if they want.
const _DEFAULT_ZOOM = 2.4;

/**
 * Modal that shows a rendered Mermaid diagram with mouse-wheel zoom and
 * click-drag pan. Used when the inline diagram is too small / dense to
 * read in the chat flow.
 *
 * Implementation notes
 * --------------------
 * • Pure CSS-transform pan/zoom — no @panzoom/panzoom or svg-pan-zoom
 *   dependency. ~80 LOC of state + event handlers does the same job for
 *   our needs.
 * • Wheel zoom is cursor-centred: the world point under the cursor stays
 *   put as the user zooms. Standard image-viewer affordance.
 * • State resets every time the modal opens so a previous zoomed-in view
 *   doesn't leak into the next diagram.
 * • Clicks on the toolbar buttons must NOT pan — they intercept the
 *   pointer-down with stopPropagation.
 */
export function FullscreenDiagram({
  open,
  onClose,
  svgMarkup,
}: FullscreenDiagramProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const svgHostRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  // Drag state lives in a ref (not state) so move/up handlers don't
  // re-render on every pixel.
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startTx: number;
    startTy: number;
  } | null>(null);

  // Fit the SVG to ~90% of the stage. Mermaid emits SVGs at their
  // intrinsic layout size (often only 600-1000 px wide), which on a
  // 1080p+ display looks "tiny" at scale=1. Measure the SVG, compute
  // the scale that fills the viewport, and use that as the default.
  const fitToView = useCallback(() => {
    const stage = stageRef.current;
    const svg = svgHostRef.current?.querySelector(
      "svg",
    ) as SVGSVGElement | null;
    if (!stage || !svg) return;

    const stageRect = stage.getBoundingClientRect();
    if (stageRect.width === 0 || stageRect.height === 0) return;

    const vb = svg.viewBox?.baseVal;
    const svgW =
      (vb && vb.width) ||
      Number.parseFloat(svg.getAttribute("width") || "0") ||
      svg.getBoundingClientRect().width ||
      800;
    const svgH =
      (vb && vb.height) ||
      Number.parseFloat(svg.getAttribute("height") || "0") ||
      svg.getBoundingClientRect().height ||
      600;
    if (svgW === 0 || svgH === 0) return;

    const fit =
      Math.min(stageRect.width / svgW, stageRect.height / svgH) * _DEFAULT_ZOOM;
    const next = Math.max(_MIN_SCALE, Math.min(_MAX_SCALE, fit));

    setScale(next);
    setTx(0);
    setTy(0);
  }, []);

  // Auto-fit whenever the modal re-opens or the diagram source changes.
  // useLayoutEffect runs synchronously after DOM mutations but before
  // the browser paints, so the user never sees the unfitted scale=1
  // view. We still rAF the measure because MUI's Dialog Paper may not
  // have its final dimensions in the same tick the open prop flips.
  useLayoutEffect(() => {
    if (!open) return;
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      if (!cancelled) fitToView();
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [open, svgMarkup, fitToView]);

  // Re-fit on viewport resize so a window resize (or zoom-in to the
  // browser) doesn't strand the diagram off-stage.
  useEffect(() => {
    if (!open) return;
    const onResize = () => fitToView();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open, fitToView]);

  // Escape-to-close — Dialog handles this too, but we re-bind so it
  // works even when focus lives inside the SVG (clicked but not yet
  // dragged).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "0" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        fitToView();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, fitToView]);

  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      e.preventDefault();
      const stage = stageRef.current;
      if (!stage) return;

      const rect = stage.getBoundingClientRect();
      // Cursor position relative to the stage centre — that's the
      // origin of our transform (translate then scale, applied to a
      // child whose origin sits at 50% 50%).
      const mouseX = e.clientX - rect.left - rect.width / 2;
      const mouseY = e.clientY - rect.top - rect.height / 2;

      const scaleDelta = -e.deltaY * _ZOOM_SENSITIVITY;
      const nextScale = Math.max(
        _MIN_SCALE,
        Math.min(_MAX_SCALE, scale * (1 + scaleDelta)),
      );
      // If we hit a clamp, the formula below would still drift — bail
      // early.
      if (nextScale === scale) return;

      // Cursor-centred zoom: the world coordinate under the cursor
      // before and after the zoom must be the same. Solve for the new
      // translation so that invariant holds.
      const scaleRatio = nextScale / scale;
      const nextTx = mouseX - (mouseX - tx) * scaleRatio;
      const nextTy = mouseY - (mouseY - ty) * scaleRatio;

      setScale(nextScale);
      setTx(nextTx);
      setTy(nextTy);
    },
    [scale, tx, ty],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Buttons inside the stage call stopPropagation; this only fires
      // on bare-canvas clicks.
      if (e.button !== 0) return;
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startTx: tx,
        startTy: ty,
      };
      // Capture the pointer so movements outside the element still
      // count as drag — feels much more responsive than relying on
      // mouseup-on-stage.
      (e.target as Element).setPointerCapture?.(e.pointerId);
    },
    [tx, ty],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      setTx(drag.startTx + (e.clientX - drag.startX));
      setTy(drag.startTy + (e.clientY - drag.startY));
    },
    [],
  );

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      // Transparent backdrop so the dark theme below the SVG shows
      // through; we still get the focus trap + ESC handling from MUI.
      PaperProps={{
        sx: {
          bgcolor: "background.default",
          backgroundImage: "none",
        },
      }}
    >
      <Box
        ref={stageRef}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          // touchAction:none disables the browser's default pinch-to-zoom
          // / pan-on-trackpad gestures so our wheel handler always wins.
          touchAction: "none",
          cursor: dragRef.current ? "grabbing" : "grab",
          userSelect: "none",
        }}
      >
        {/*
         * Inner stage that actually carries the transform. Origin sits at
         * 50% 50% so the initial 1× view is centred regardless of the
         * SVG's intrinsic size. We don't constrain max-width here (unlike
         * the inline view) — the modal exists precisely to let the user
         * zoom past the page width.
         */}
        <Box
          ref={svgHostRef}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transformOrigin: "0 0",
            transform: `translate(-50%, -50%) translate(${tx}px, ${ty}px) scale(${scale})`,
            transition: dragRef.current ? "none" : "transform 80ms ease-out",
            "& svg": {
              display: "block",
              // Modal SVG should ignore the inline view's max-width — we
              // want the user to be able to zoom past the viewport.
              maxWidth: "none",
              height: "auto",
            },
          }}
          // The SVG markup is captured from MermaidDiagram's container
          // AFTER DOMPurify ran on the upstream side, so injecting it
          // here is safe.
          dangerouslySetInnerHTML={{ __html: svgMarkup }}
        />

        {/* Toolbar — close + reset, mirrors the inline diagram's chrome. */}
        <Box
          onPointerDown={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            display: "flex",
            gap: 0.5,
            padding: "4px",
            borderRadius: 1.5,
            bgcolor: "rgba(10, 16, 34, 0.78)",
            border: "1px solid",
            borderColor: "border.default",
          }}
        >
          <Tooltip title="Reset view (⌘0)" arrow>
            <IconButton
              onClick={fitToView}
              size="small"
              aria-label="Reset view"
              sx={{ color: "text.secondary" }}
            >
              <CenterFocusStrongIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close (Esc)" arrow>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close fullscreen"
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Bottom-left zoom indicator — gives the user a sense of scale
            without a heavy zoom-controls panel. */}
        <Box
          sx={{
            position: "absolute",
            bottom: 12,
            left: 12,
            padding: "4px 10px",
            borderRadius: 1,
            bgcolor: "rgba(10, 16, 34, 0.78)",
            border: "1px solid",
            borderColor: "border.default",
            color: "text.secondary",
            fontSize: "0.72rem",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            pointerEvents: "none",
          }}
        >
          {(scale * 100).toFixed(0)}% · scroll to zoom · drag to pan
        </Box>
      </Box>
    </Dialog>
  );
}
